﻿type Coordinate = number[];

import CoordinateParser from "coordinate-parser";

import { CoordinateDirection, CoordinateNotation } from '../interfaces/CoordinateNotation';

class CoordinateUtilities {

    public static parseLonLatCoordinate(value: string): Coordinate | undefined {
        try {
            const position = new CoordinateParser(value);
            return [position.getLongitude(), Math.min(90, Math.max(-90, position.getLatitude()))];
        }
        catch {
            return undefined;
        }
    }

    public static formatLonLatCoordinate(coordinate: Coordinate, notation: CoordinateNotation, decimals: number = 2) {
        return this.getFormattedLat(coordinate[1], notation, decimals) + ", " + this.getFormattedLon(coordinate[0], notation, decimals);
    }

    public static getFormattedLat(degrees: number, notation: CoordinateNotation, decimals: number = 2) {
        return this.getFormattedCoordinate(degrees, ["N", "S"], notation, decimals);
    }

    public static getFormattedLon(degrees: number, notation: CoordinateNotation, decimals: number = 2, keepBetweenDateLines: boolean = false) {
        if (keepBetweenDateLines) {
            degrees = ((degrees + 180) % 360);
            degrees = degrees >= 0 ? degrees - 180 : degrees + 180
        }
        return this.getFormattedCoordinate(degrees, ["E", "W"], notation, decimals);
    }

    public static compass(dir: number, reverse: boolean = false) {
        if (dir == null) return "";
        if (reverse) dir += 180;
        const strings = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
        return strings[Math.round(strings.length * this.angle0To360(dir) / 360) % strings.length];
    }

    private static getFormattedCoordinate(degrees: number, directions: [CoordinateDirection, CoordinateDirection], notation: CoordinateNotation, decimals: number) {
        const direction = degrees >= 0 ? directions[0] : directions[1];
        const precision = 6;
        degrees = Math.abs(this.setPrecision(degrees, precision));

        const deg_i = Math.floor(degrees);
        const deg_i_s = this.formatDeg(deg_i, 0) + "\u00B0";
        const min_d = this.setPrecision((degrees - deg_i) * 60, precision);

        var value;
        switch (notation) {
            case "Sec":
                const min_i = Math.floor(min_d);
                const min_i_s = this.formatMinSec(min_i, 0) + "'";
                const sec_d = this.setPrecision((min_d - min_i) * 60, precision);
                const sec_d_s = this.formatMinSec(sec_d, decimals) + '"'
                value = deg_i_s + min_i_s + sec_d_s;
                break;
            case "Min":
                const min_d_s = this.formatMinSec(min_d, 2 + decimals) + "'";
                value = deg_i_s + min_d_s;
                break;
            case "Deg":
            default:
                const deg_d_s = this.formatDeg(degrees, 4 + decimals) + "\u00B0";
                value = deg_d_s;
                break;
        }

        return `${value} ${direction}`;
    }

    private static setPrecision(value: number, decimals: number) {
        const precision = Math.pow(10, decimals)
        return Math.round(value * precision) / precision;
    }

    private static formatMinSec(n: number, dec: number) {
        return Math.floor(n).toString().length === 1 ? "0" + n.toFixed(dec) : n.toFixed(dec);
    }

    private static formatDeg(n: number, dec: number) {
        var l = Math.floor(n).toString().length;
        var prefix = ""
        if (l === 1)
            prefix = "  "
        else if (l === 2)
            prefix = " ";

        return prefix + n.toFixed(dec);
    }

    private static angle0To360(deg: number) {
        if (deg <= 360 && deg >= 0)
            return deg;
        deg = deg % 360.0;
        return deg < 0.0 ? deg + 360.0 : deg;
    }
}

export default CoordinateUtilities;